/** The confidence scores an audit run produces for a ticket. */
export type AuditScoreType = "FIELD_VALIDATION_CONFIDENCE" | "BUSINESS_VALIDATION_CONFIDENCE";

export type AuditFindingSeverity = "INFO" | "WARNING" | "ERROR" | "CRITICAL";

/** The latest audit of one object, summarized: `AuditObjectAuditSummaryDTO` on the server. */
export type AuditObjectAuditSummary = {
  target: { objectType?: string; objectId: string };
  hasAuditRun: boolean;
  latestRunId?: string;
  latestRunStartedAtDttm?: number;
  latestRunCompletedAtDttm?: number;
  /** 0–1 per score type; a type is missing when the run could not score it. */
  confidences: Partial<Record<AuditScoreType, number>>;
  scoreStatuses: Partial<Record<AuditScoreType, string>>;
  findingCount: number;
  /** Findings that block the ticket from moving on. */
  blockingFindingCount: number;
  findingCountsBySeverity: Partial<Record<AuditFindingSeverity, number>>;
};

export type TtaAuditSummaryRequest = {
  targets: { objectId: string; tenantCode?: string }[];
  scoreTypes?: AuditScoreType[];
};

/** One thing an audit rule found wrong, or worth noting, on a ticket: `AuditFindingDTO` on the server. */
export type AuditFinding = {
  id: string;
  runId?: string;
  ruleKey: string;
  ruleVersion?: number;
  auditType?: string;
  severity: AuditFindingSeverity;
  /** Stops the ticket from moving on until resolved. */
  blocking: boolean;
  title: string;
  message?: string;
  /** Where on the ticket the finding points, e.g. ["pickupTransport", "quantityDecimal"]. */
  targetPath?: string[];
  createDttm?: number;
};

/** A ticket's audit in full: `AuditObjectAuditViewDTO`, of which the workspace reads the findings. */
export type AuditObjectAuditView = {
  target: { objectType?: string; objectId: string };
  hasAuditRun: boolean;
  latestRun?: { runId: string; startedDttm?: number; completedDttm?: number; findings?: AuditFinding[]; scores?: AuditScore[] };
  findings: AuditFinding[];
  scores?: AuditScore[];
};

/** One confidence score from an audit run, with what went into it: `AuditScoreDTO` on the server. */
export type AuditScore = {
  scoreType: AuditScoreType | string;
  /** `NO_EVIDENCE` when no applicable rule could be evaluated. */
  status?: string;
  /** 0–1. */
  confidence?: number;
  /** What the weighted rules came to before any cap was applied. */
  uncappedConfidence?: number;
  impacts?: unknown[];
  contributions?: unknown[];
  appliedCaps?: { ruleKey?: string; ruleName?: string; maximumConfidence?: number }[];
};

/** The body WaterGo sends to audit a ticket on request: `AuditObjectAuditRunRequest.create(false)`. */
export const USER_REQUESTED_AUDIT_RUN = {
  triggerType: "USER_REQUEST",
  scope: { attributes: {} },
  configOverrides: [],
  dryRun: false,
  requestedAuditTypes: [],
  requestedScoreTypes: [],
  requestedRuleKeys: [],
  requestedTags: [],
  responseOptions: { includeLatestRun: true, includeRuleResults: true, includeFindings: true, includeScores: true },
} as const;
